import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import React, { useEffect, useRef } from "react";
import { oktaAuth, useUser } from "../contexts/UserContext";
import { isOAuthCallbackInProgress } from "../utils/OktaAuthUtil";

interface HttpInterceptorProps {
  children: React.ReactNode;
}

const HttpInterceptor = ({ children }: HttpInterceptorProps) => {
  const { logout } = useUser();
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    axiosRetry(axios, {
      retries: 3,
      retryCondition: (error) => {
        console.log("Error fetching from API will retry...");
        return !!error?.response && (error.response.status === 401 || error.response.status === 403);
      }, // only retry on unauthenticated errors to prevent retrying 500s unless that is wanted
      retryDelay: (retryCount) => retryCount * 1000, // wait 1 second between retrys
    });

    const requestInterceptorId = axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
      if (config.url && config.headers) {
        if (config.url.indexOf("/oauth/token") < 0 || config.headers.addToken) {
          config.headers.Authorization = `Bearer ${await oktaAuth.getOrRenewAccessToken()}`;
        }
      }
      return config;
    });

    const responseInterceptorId = axios.interceptors.response.use(
      (resp: AxiosResponse) => {
        if (!resp) return null;
        return resp;
      },
      (err: AxiosError) => {
        if (!!err.response) {
          if ((err.response.status === 401 || err.response.status === 403) && !isOAuthCallbackInProgress()) {
            logoutRef.current();
          }

          return Promise.reject(err);
        }
        return Promise.reject(err);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  return <div>{children}</div>;
};

export default HttpInterceptor;
